import { Box, Typography, useTheme, Divider } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { format } from 'date-fns';

type RafflePreviousWinnersProps = {
  name: string;
  winningDate: Date;

  // Fixed prize for now
  prizeLabel?: string;

  // Optional
  discordUsername?: string;
};

const RafflePreviousWinners = ({
  name,
  winningDate,
  prizeLabel = 'A$10,000 in INC',
  discordUsername
}: RafflePreviousWinnersProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: 5,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid rgba(90, 255, 240, 0.12)',
        boxShadow: 'inset 0 0 0 1px rgba(124,255,250,0.10)',
        background: 'rgba(10, 16, 18, 0.72)',
        backdropFilter: 'blur(12px)'
      }}
    >
      {/* Soft vignette + subtle blue gradient */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(760px 420px at 20% 0%, rgba(255,255,255,0.08), transparent 60%),' +
            'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(22,70,255,0.10) 100%)',
          pointerEvents: 'none'
        }}
      />

      {/* CONTENT */}
      <Box sx={{ p: { xs: 2.2, sm: 2.6, md: 3 }, position: 'relative' }}>
        {/* Winner + Prize row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            alignItems: 'flex-start'
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: { xs: 22, sm: 24, md: 26 },
                  color: 'rgba(220,255,255,0.92)'
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: 20, sm: 22, md: 24 },
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  color: 'rgba(230,255,255,0.95)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {name}
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 0.4,
                fontSize: { xs: 13, sm: 14, md: 15 },
                fontWeight: 600,
                color: 'rgba(255,255,255,0.42)'
              }}
            >
              Won on {format(winningDate, 'MMM d, yyyy')}
            </Typography>

            {!!discordUsername && (
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: { xs: 13, sm: 14, md: 14 },
                  fontWeight: 800,
                  color: 'rgba(124,255,250,0.75)'
                }}
              >
                Discord: {discordUsername}
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 22, sm: 26, md: 28 },
                fontWeight: 950,
                letterSpacing: '-0.02em',
                color: 'rgba(230,255,255,0.95)'
              }}
            >
              {prizeLabel}
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                fontSize: { xs: 11, sm: 12, md: 12 },
                fontWeight: 700,
                color: 'rgba(255,255,255,0.45)'
              }}
            >
              Prize
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.10)' }} />

        {/* Verified row (keep for trust) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: { xs: 20, sm: 22, md: 22 },
              color: 'rgba(220,255,255,0.92)'
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: 16, sm: 17, md: 18 },
              fontWeight: 900,
              color: 'rgba(230,255,255,0.95)'
            }}
          >
            Verified Winner
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RafflePreviousWinners;
